import { useParams } from "react-router-dom";
import { useSupportCaseManagement } from "@/hooks/useSupportCaseManagement";
import { getSupportCategoryLabel } from "@/utils/supportCategoryLabel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertCircle,
  Wrench,
  Calendar,
  Clock,
  FileText,
  Send,
  User,
  MessageSquare,
  Upload,
  Trash2,
} from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { format } from "date-fns";
import { sv, enGB } from "date-fns/locale";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n";
import { CommentForm } from "@/admin/leads/comments/CommentForm";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import { useFile } from "@/hooks/useFile";
import { useSupportConversation } from "@/hooks/useSupportConversation";
import {
  getSupportStatusColor,
  getSupportPriorityColor,
  getSupportTypeIcon,
} from "@/utils/supportUiUtils";
import { SupportCaseBadgePicker } from "@/admin/pages/SupportCaseBadgePicker";
import { ComplaintFollowUpSummary } from "@/components/support/ComplaintFollowUpSummary";

export default function SupportTicketPage() {
  const { id: supportId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { downloadFile } = useFile();
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const dateLocale = language === "en" ? enGB : sv;

  const [savingField, setSavingField] = useState<"status" | "priority" | null>(
    null,
  );

  const {
    supportCase,
    isLoadingCase,
    caseError,
    updateSupportCaseAsync,
    addCommentAsync,
    addDocumentAsync,
    removeDocumentAsync,
    isAddingComment,
    isAddingDocument,
    isRemovingDocument,
  } = useSupportCaseManagement(undefined, supportId || "");

  const conversationItems = useSupportConversation(
    supportCase?.comments,
    supportCase?.messages,
    supportCase
      ? {
          includeInternal: true,
          customerId: supportCase.customerId,
        }
      : undefined,
  );

  const handleAddComment = async (
    text: string,
    _isPublic: boolean,
    mentions: string[],
  ) => {
    if (!text.trim() || !supportCase) return;

    try {
      await addCommentAsync({
        supportCaseId: supportCase.id,
        content: text,
        isInternal: false,
        notifyColleagues: mentions,
      });
      toast.success(L.commentAdded ?? "Kommentar tillagd");
    } catch {
      toast.error(L.commentAddError ?? "Kunde inte lägga till kommentar");
    }
  };

  const handleStatusChange = async (nextStatus: string) => {
    if (!supportCase || nextStatus === supportCase.status) return;
    setSavingField("status");
    try {
      await updateSupportCaseAsync({
        caseId: supportCase.id,
        updates: {
          status: nextStatus as
            | "not_started"
            | "in_progress"
            | "resolved"
            | "closed",
        },
      });
      toast.success(L.changesSaved ?? "Ändringar sparade");
    } catch {
      toast.error(L.changesSaveError ?? "Kunde inte spara ändringar");
    } finally {
      setSavingField(null);
    }
  };

  const handlePriorityChange = async (nextPriority: string) => {
    if (!supportCase || nextPriority === supportCase.priority) return;
    setSavingField("priority");
    try {
      await updateSupportCaseAsync({
        caseId: supportCase.id,
        updates: {
          priority: nextPriority as "low" | "medium" | "high",
        },
      });
      toast.success(L.changesSaved ?? "Ändringar sparade");
    } catch {
      toast.error(L.changesSaveError ?? "Kunde inte spara ändringar");
    } finally {
      setSavingField(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Check file size (10MB limit)
    const oversizedFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(
        (
          L.filesTooLarge ?? "Följande filer är för stora (max 10 MB): {files}"
        ).replace("{files}", oversizedFiles.map((f) => f.name).join(", ")),
      );
      return;
    }

    if (!supportCase) return;

    // Upload each file individually
    for (const file of files) {
      try {
        await addDocumentAsync({
          supportCaseId: supportCase.id,
          document: file,
        });
        toast.success(
          (L.fileUploaded ?? 'Fil "{filename}" uppladdad').replace(
            "{filename}",
            file.name,
          ),
        );
      } catch (error) {
        toast.error(
          (L.fileUploadError ?? 'Kunde inte ladda upp "{filename}"').replace(
            "{filename}",
            file.name,
          ),
        );
      }
    }

    // Clear the input
    e.target.value = "";
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!supportCase) return;

    try {
      await removeDocumentAsync({
        supportCaseId: supportCase.id,
        documentId: fileId,
      });
      toast.success(L.fileDeleted ?? "Fil borttagen");
    } catch {
      toast.error(L.fileDeleteError ?? "Kunde inte ta bort fil");
    }
  };

  if (isLoadingCase) {
    return (
      <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{L.loadingCase ?? "Laddar ärende..."}</div>
        </div>
      </div>
    );
  }

  if (caseError || !supportCase) {
    return (
      <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-500">
            {caseError
              ? (L.errorLoadingGeneric ??
                "Kunde inte ladda ärendet. Försök igen senare.")
              : (L.caseNotFound ?? "Ärende hittades inte")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-6 py-2 sm:py-6 max-w-5xl">
      <BtnNavigateBack
        label="Support"
        to="/admin/support"
        className="mb-3 self-start"
      />

      <Card className="glass-card p-3 sm:p-4 min-w-0 overflow-hidden">
        <div className="space-y-4 sm:space-y-6">
          {/* Header Card */}
          <div>
            <CardHeader className="p-0 pb-2 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  {getSupportTypeIcon(supportCase.type)}
                  <div className="min-w-0 flex-1">
                    <h1 className="text-base sm:text-2xl font-bold leading-tight break-all">
                      {(L.caseNumber ?? "Ärende #{id}").replace(
                        "{id}",
                        supportCase.caseRef || supportCase.id,
                      )}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs sm:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1 min-w-0">
                        <User className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{supportCase.customerName}</span>
                      </div>
                      {supportCase.vehicleRegNr ? (
                        <div className="flex items-center gap-1">
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span>{supportCase.vehicleRegNr}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {format(new Date(supportCase.created), "dd/MM/yyyy", {
                            locale: dateLocale,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                  <SupportCaseBadgePicker
                    value={supportCase.status}
                    title={L.status ?? "Status"}
                    badgeClassName={getSupportStatusColor(supportCase.status)}
                    isSaving={savingField === "status"}
                    disabled={savingField !== null}
                    onChange={handleStatusChange}
                    options={[
                      {
                        value: "not_started",
                        label: L.notStarted ?? "Ej påbörjad",
                      },
                      {
                        value: "in_progress",
                        label: L.inProgress ?? "Pågående",
                      },
                      { value: "resolved", label: L.resolved ?? "Löst" },
                      { value: "closed", label: L.closed ?? "Stängd" },
                    ]}
                  />
                  <SupportCaseBadgePicker
                    value={supportCase.priority}
                    title={L.priority ?? "Prioritet"}
                    badgeClassName={getSupportPriorityColor(supportCase.priority)}
                    isSaving={savingField === "priority"}
                    disabled={savingField !== null}
                    onChange={handlePriorityChange}
                    options={[
                      { value: "low", label: L.low ?? "Låg" },
                      { value: "medium", label: L.medium ?? "Medium" },
                      { value: "high", label: L.high ?? "Hög" },
                    ]}
                  />
                </div>
              </div>
            </CardHeader>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="info" className="space-y-3 sm:space-y-4 mt-2 sm:mt-4">
            <TabsList className="w-full h-auto flex overflow-x-auto justify-start gap-0.5 p-1">
                <TabsTrigger value="info" className="text-xs sm:text-sm px-2.5 py-1.5 shrink-0">
                  {L.information ?? "Information"}
                </TabsTrigger>
                <TabsTrigger value="history" className="text-xs sm:text-sm px-2.5 py-1.5 shrink-0">
                  {L.history ?? "Historik"}
                </TabsTrigger>
                <TabsTrigger value="comments" className="text-xs sm:text-sm px-2.5 py-1.5 shrink-0">
                  {L.comments ?? "Kommentarer"}
                </TabsTrigger>
                <TabsTrigger value="documents" className="text-xs sm:text-sm px-2.5 py-1.5 shrink-0">
                  {L.documents ?? "Dokument"}
                </TabsTrigger>
            </TabsList>

            {/* Information Tab */}
            <TabsContent value="info" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {L.caseInformation ?? "Ärendeinformation"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.customer ?? "Kund"}
                      </label>
                      <p className="text-sm mt-1">{supportCase.customerName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.email ?? "Email"}
                      </label>
                      <p className="text-sm mt-1">
                        {supportCase.customerEmail}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.type ?? "Typ"}
                      </label>
                      <div className="mt-1">
                        <Badge variant="outline">
                          {supportCase.type === "complaint"
                            ? (L.complaint ?? "Reklamation")
                            : (L.service ?? "Service")}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.category ?? "Kategori"}
                      </label>
                      <p className="text-sm mt-1">
                        {getSupportCategoryLabel(supportCase.category, language)}
                      </p>
                    </div>
                    {supportCase.caseRef && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {L.caseReferenceNumber ?? "Ärendenummer"}
                        </label>
                        <p className="text-sm mt-1">{supportCase.caseRef}</p>
                      </div>
                    )}
                    {supportCase.vehicleRegNr && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {L.registrationNumber ?? "Registreringsnummer"}
                        </label>
                        <p className="text-sm mt-1">
                          {supportCase.vehicleRegNr}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.created ?? "Skapad"}
                      </label>
                      <p className="text-sm mt-1">
                        {format(
                          new Date(supportCase.created),
                          "dd/MM/yyyy 'kl' HH:mm",
                          { locale: dateLocale },
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {L.lastUpdated ?? "Senast uppdaterad"}
                      </label>
                      <p className="text-sm mt-1">
                        {format(
                          new Date(supportCase.lastUpdated),
                          "dd/MM/yyyy 'kl' HH:mm",
                          { locale: dateLocale },
                        )}
                      </p>
                    </div>
                  </div>

                  <ComplaintFollowUpSummary
                    category={supportCase.category}
                    damageLocation={supportCase.damageLocation}
                    damageType={supportCase.damageType}
                    engineComponent={supportCase.engineComponent}
                    interiorSection={supportCase.interiorSection}
                    wheelType={supportCase.wheelType}
                    language={language}
                  />

                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      {L.description ?? "Beskrivning"}
                    </label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {supportCase.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{L.history ?? "Historik"}</CardTitle>
                </CardHeader>
                <CardContent>
                  {supportCase.historyEvents &&
                  supportCase.historyEvents.length > 0 ? (
                    <div className="space-y-4">
                      {supportCase.historyEvents.map((event) => {
                        const isFromCustomer =
                          event.userName === supportCase.customerName;
                        const isFromSystem =
                          event.userName === "System" ||
                          event.userName === "Admin User";

                        return (
                          <div
                            key={event.id}
                            className={`flex ${
                              isFromCustomer ? "justify-start" : "justify-end"
                            }`}
                          >
                            <div
                              className={`max-w-[70%] ${
                                isFromCustomer ? "mr-auto" : "ml-auto"
                              }`}
                            >
                              <div
                                className={`p-3 rounded-lg ${
                                  isFromCustomer
                                    ? "bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
                                    : isFromSystem
                                      ? "bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
                                      : "bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`text-sm font-medium ${
                                      isFromCustomer
                                        ? "text-blue-800 dark:text-blue-200"
                                        : isFromSystem
                                          ? "text-gray-800 dark:text-gray-200"
                                          : "text-green-800 dark:text-green-200"
                                    }`}
                                  >
                                    {event.action}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {format(
                                      new Date(event.timestamp),
                                      "dd/MM/yyyy 'kl' HH:mm",
                                      { locale: dateLocale },
                                    )}
                                  </span>
                                </div>
                                <p
                                  className={`text-xs ${
                                    isFromCustomer
                                      ? "text-blue-600 dark:text-blue-300"
                                      : isFromSystem
                                        ? "text-gray-600 dark:text-gray-300"
                                        : "text-green-600 dark:text-green-300"
                                  }`}
                                >
                                  {L.by ?? "av"} {event.userName}
                                </p>
                                {event.details && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    {event.details.oldValue &&
                                      event.details.newValue && (
                                        <p>
                                          {L.from ?? "Från"}:{" "}
                                          {event.details.oldValue} →{" "}
                                          {L.to ?? "Till"}:{" "}
                                          {event.details.newValue}
                                        </p>
                                      )}
                                    {event.details.fileName ? (
                                      <p>{event.details.fileName}</p>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {L.noHistoryAvailable ?? "Ingen historik tillgänglig"}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comments Tab — full thread (customer messages + all comments) */}
            <TabsContent value="comments" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{L.comments ?? "Kommentarer"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {conversationItems.length > 0 ? (
                    <div className="space-y-4">
                      {conversationItems.map((item) => (
                        <div key={item.id} className="border rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium">
                              {item.authorName}
                            </span>
                            <span className="text-xs text-gray-500">
                              {format(
                                new Date(item.createdAt),
                                "dd/MM/yyyy 'kl' HH:mm",
                                { locale: dateLocale },
                              )}
                            </span>
                            {item.isInternal && (
                              <Badge variant="secondary" className="text-xs">
                                {L.internal ?? "Intern"}
                              </Badge>
                            )}
                            {item.isFromCustomer && (
                              <Badge variant="outline" className="text-xs">
                                {L.customer ?? "Kund"}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm whitespace-pre-wrap">
                            {item.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {L.noCommentsYet ?? "Inga kommentarer än"}
                    </div>
                  )}

                  {/* Add Comment Section */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-medium mb-3">
                      {L.addComment ?? "Lägg till kommentar"}
                    </h3>
                    <CommentForm
                      onSubmit={handleAddComment}
                      isSubmitting={isAddingComment}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{L.documents ?? "Dokument"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      {L.uploadDocument ?? "Ladda upp dokument"}
                    </p>
                    <FileUploadTrigger
                      accept="*/*"
                      multiple
                      disabled={isAddingDocument}
                      onFilesSelected={(files) => {
                        const dt = new DataTransfer();
                        files.forEach((f) => dt.items.add(f));
                        void handleFileUpload({
                          target: { files: dt.files },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      {(open) => (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isAddingDocument}
                          className="cursor-pointer"
                          onClick={open}
                        >
                          {isAddingDocument
                            ? (L.uploading ?? "Laddar upp...")
                            : (L.selectFiles ?? "Välj filer")}
                        </Button>
                      )}
                    </FileUploadTrigger>
                  </div>

                  {/* Existing Files */}
                  {supportCase.files && supportCase.files.length > 0 ? (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">
                        {L.uploadedDocuments ?? "Uppladdade dokument:"}
                      </h4>
                      {supportCase.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-3 p-3 border rounded-lg"
                        >
                          <FileText className="h-5 w-5 text-gray-400" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB{" "}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (file.url) {
                                  try {
                                    const filename = await downloadFile(
                                      file.url,
                                      file.name,
                                    );
                                    toast.success(
                                      (
                                        L.downloadStarted ??
                                        "Nedladdning startad: {filename}"
                                      ).replace("{filename}", filename),
                                    );
                                  } catch (error) {
                                    const errorMessage =
                                      error instanceof Error
                                        ? error.message
                                        : (L.unknownError ?? "Okänt fel");
                                    toast.error(
                                      (
                                        L.downloadError ??
                                        "Kunde inte ladda ner {filename}: {error}"
                                      )
                                        .replace("{filename}", file.name)
                                        .replace("{error}", errorMessage),
                                    );
                                  }
                                }
                              }}
                            >
                              {L.download ?? "Ladda ner"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteFile(file.id)}
                              disabled={isRemovingDocument}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {L.noDocumentsUploaded ?? "Inga dokument uppladdade"}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </Card>
    </div>
  );
}
